import React from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingUp, Users } from 'lucide-react';

interface TeamProgressChartProps {
  current: number;
  target: number;
  activeSdrs: number;
}

const TeamProgressChart: React.FC<TeamProgressChartProps> = ({ current, target, activeSdrs }) => {
  const percentage = Math.min((current / target) * 100, 100);
  const radius = 140;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const getColorByProgress = (progress: number) => {
    if (progress >= 100) return { stroke: '#10b981', bg: 'from-green-500 to-emerald-500' };
    if (progress >= 75) return { stroke: '#00C3FF', bg: 'from-cyan-500 to-blue-500' };
    if (progress >= 50) return { stroke: '#f59e0b', bg: 'from-yellow-500 to-orange-500' };
    return { stroke: '#ef4444', bg: 'from-red-500 to-pink-500' };
  };

  const colors = getColorByProgress(percentage);

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div className="bg-cyan-50 p-2 rounded-lg">
            <Target className="h-5 w-5 text-cyan-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Objectif Équipe</h3>
            <p className="text-xs text-gray-500">Performance du jour</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center bg-blue-50 px-3 py-2 rounded-lg mb-4">
        <Users className="h-4 w-4 text-blue-600" />
        <span className="text-sm font-semibold text-blue-700 ml-2">{activeSdrs} SDRs actifs</span>
      </div>

      <div className="flex items-center justify-center flex-1 py-4">
        <div className="relative">
          <svg width="220" height="220" className="transform -rotate-90">
            <circle
              cx="110"
              cy="110"
              r={100}
              stroke="#f3f4f6"
              strokeWidth="16"
              fill="none"
            />
            <motion.circle
              cx="110"
              cy="110"
              r={100}
              stroke={colors.stroke}
              strokeWidth="16"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 100}
              initial={{ strokeDashoffset: 2 * Math.PI * 100 }}
              animate={{ strokeDashoffset: 2 * Math.PI * 100 - (percentage / 100) * 2 * Math.PI * 100 }}
              transition={{ duration: 2, ease: 'easeInOut' }}
            />
          </svg>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className={`bg-gradient-to-br ${colors.bg} bg-clip-text text-transparent`}
              >
                <div className="text-5xl font-black">{current}</div>
              </motion.div>
              <div className="text-gray-400 text-base font-semibold mt-1">/ {target}</div>
              <div className="text-gray-500 text-xs mt-1">RDVs pris</div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2 mt-4">
        <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-cyan-700">{Math.round(percentage)}%</div>
          <div className="text-xs text-gray-600 mt-0.5 font-medium">Progression</div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-700">{Math.max(target - current, 0)}</div>
          <div className="text-xs text-gray-600 mt-0.5 font-medium">Restant</div>
        </div>
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-yellow-700">
            {activeSdrs > 0 ? (current / activeSdrs).toFixed(1) : '0'}
          </div>
          <div className="text-xs text-gray-600 mt-0.5 font-medium">Moy./SDR</div>
        </div>
      </div>

      {percentage >= 100 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg p-3 text-center"
        >
          <div className="flex items-center justify-center space-x-2 text-white">
            <TrendingUp className="h-4 w-4" />
            <span className="font-bold text-sm">Objectif atteint !</span>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default TeamProgressChart;
